// components/AssignLandlordPropertiesDialog.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Box,
  Paper,
  Divider,
  Avatar,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import {
  Email,
  Phone,
  Search,
  Warning,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import stringToColor from "../services/stringToColor";
import { useUser } from "../provider/AuthProvider";
import axios from "axios";
import { useTranslation } from "react-i18next";

const AssignLandlordPropertiesDialog = ({
  open,
  onClose,
  landlord,
  onAssigned,
}) => {
  const [units, setUnits] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { user } = useUser();
  const { t } = useTranslation();
  // Fetch all units when dialog opens
  useEffect(() => {
    if (open) fetchUnits();
  }, [open]);

  // Enrich selected units with full info from all units
  useEffect(() => {
    // landlord.Unit is your assigned list (from backend)
    if (landlord?.Unit && units.length > 0) {
      // Flatten fetched units for lookup
      const allFetchedUnits = units.flatMap((b) =>
        b.units.map((unit) => ({
          ...unit,
          building: b.building_name,
          location: b.location,
          type: b.type,
        }))
      );
      // Merge
      const enriched = landlord.Unit.map((p) => {
        const match = allFetchedUnits.find((u) => u.id === p.id);
        return match ? { ...match } : p;
      });
      setSelectedUnits(enriched);
    } else if (landlord?.Unit) {
      setSelectedUnits(landlord.Unit);
    } else {
      setSelectedUnits([]);
    }
  }, [landlord, units]);

  const fetchUnits = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/compound/units/${user.id}`
      );
      setUnits(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  // Flatten all units for easier searching
  const allUnits = useMemo(
    () =>
      units.flatMap((b) =>
        b.units.map((unit) => ({
          ...unit,
          building: b.building_name,
          location: b.location,
          type: b.type,
        }))
      ),
    [units]
  );

  // Group units by building
  function groupUnitsByBuilding(units) {
    const groups = {};
    units.forEach((unit) => {
      const key = `${unit.building}__${unit.location}__${unit.type}`;
      if (!groups[key]) {
        groups[key] = {
          building: unit.building,
          location: unit.location,
          type: unit.type,
          units: [],
        };
      }
      groups[key].units.push(unit);
    });
    return Object.values(groups);
  }

  // Filtered available units (exclude already selected)
  const filteredAvailableUnits = useMemo(
    () =>
      allUnits.filter(
        (unit) =>
          !selectedUnits.some((su) => su.id === unit.id) &&
          (unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            unit.building.toLowerCase().includes(searchTerm.toLowerCase()))
      ),
    [allUnits, selectedUnits, searchTerm]
  );

  // Move unit to selected (left panel)
  const handleSelectUnit = (unit) => {
    setSelectedUnits((prev) => [...prev, unit]);
  };

  // Remove unit from selected (move back to available)
  const handleRemoveUnit = (unit) => {
    setSelectedUnits((prev) => prev.filter((u) => u.id !== unit.id));
  };

  // On Confirm, send all selected unit IDs to backend
  const handleSave = async () => {
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/compound/assignLandlordProperties`,
      {
        landlordId: landlord.user_id,
        unitIds: selectedUnits.map((u) => u.id),
      }
    );
    onAssigned?.();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Assign Properties to Landlord</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} columns={{ xs: 2, sm: 6, md: 8 }}>
          {/* LEFT: Selected Units */}
          <Grid item size={{ xs: 2, sm: 2, md: 3 }}>
            <Paper
              sx={{ p: 3, borderRadius: 3, minHeight: 540, height: "100%" }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                mb={2}
                className="notranslate"
              >
                <Avatar
                  sx={{
                    width: 72,
                    height: 72,
                    mb: 2,
                    bgcolor: stringToColor(landlord?.username),
                    fontSize: 32,
                  }}
                >
                  {landlord?.username?.[0]?.toUpperCase() || "?"}
                </Avatar>
                <Typography variant="h6" textAlign="center">
                  {landlord?.username}
                </Typography>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                mb={1}
                className="notranslate"
              >
                <Email fontSize="small" color="action" />
                <Typography variant="body2">{landlord?.email}</Typography>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                mb={2}
                className="notranslate"
              >
                <Phone fontSize="small" color="action" />
                <Typography variant="body2" dir="ltr">
                  {landlord?.phone}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Typography fontWeight={600} gutterBottom className="notranslate">
                {t("assigned_properties")} ({selectedUnits.length})
              </Typography>
              <Box
                sx={{
                  maxHeight: "30vh",
                  overflowY: "auto",
                  p: 1,
                  borderRadius: 2,
                }}
              >
                {selectedUnits.length === 0 ? (
                  <Typography align="center" mt={4}>
                    Please select the Unit.
                  </Typography>
                ) : (
                  <List dense>
                    {selectedUnits.map((unit) => (
                      <ListItem
                        key={unit.id}
                        sx={{
                          backgroundColor: "rgba(0,0,0,0.1)",
                          mb: 1,
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="remove"
                            onClick={() => handleRemoveUnit(unit)}
                            size="small"
                          >
                            <DeleteIcon color="error" fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={
                            <Box
                              display="flex"
                              gap={1}
                              alignItems="center"
                              className="notranslate"
                            >
                              <Typography
                                variant="body2"
                                sx={{ mr: 0.5 }}
                                className="notranslate"
                              >
                                {unit.name}
                              </Typography>
                              <Chip label={unit.building} size="small" />
                            </Box>
                          }
                          secondary={
                            <Box gap={1} flexWrap="wrap">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {unit.type?.toLowerCase() === "tower"
                                  ? `Floor ${unit.floor}, Flat ${unit.flat_number}`
                                  : `Block ${unit.floor}, Villa ${unit.flat_number}`}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Paper>
          </Grid>
          {/* RIGHT: Available Units, grouped by building */}
          <Grid item size={{ xs: 2, sm: 4, md: 5 }}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Box mb={2} display="flex" alignItems="center">
                <Search sx={{ mr: 1 }} />
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search by unit name or building"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Box>
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ maxHeight: 500, overflowY: "auto", pr: 1 }}>
                {loading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  (() => {
                    const grouped = groupUnitsByBuilding(
                      filteredAvailableUnits
                    );
                    if (grouped.length === 0) {
                      return (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          py={5}
                        >
                          <Warning
                            sx={{ fontSize: 64, mb: 2 }}
                            color="warning"
                          />
                          <Typography
                            variant="h6"
                            color="warning"
                            textAlign="center"
                          >
                            There are no available units.
                            <br />
                            Please add new units or check again later.
                          </Typography>
                        </Box>
                      );
                    }
                    return grouped.map((building) => (
                      <Box
                        key={`${building.building}_${building.location}_${building.type}`}
                        mb={2}
                      >
                        <Typography
                          variant="h6"
                          gutterBottom
                          className="notranslate"
                        >
                          {building.building} - {building.location}
                        </Typography>
                        <List dense>
                          {building.units.map((unit) => (
                            <ListItem
                              button
                              key={unit.id}
                              onClick={() => handleSelectUnit(unit)}
                              sx={{
                                backgroundColor: "rgba(0,0,0,0.1)",
                                border: "2px solid",
                                borderColor: unit.owner
                                  ? "#1976d2"
                                  : "transparent",
                                borderRadius: 1,
                                mt: 1,
                              }}
                            >
                              <ListItemText
                                primary={
                                  <React.Fragment>
                                    <Typography className="notranslate">
                                      {unit.name}
                                    </Typography>
                                  </React.Fragment>
                                }
                                secondary={
                                  <React.Fragment>
                                    <Box display="flex" gap={15}>
                                      <Box gap={5}>
                                        <Box
                                          display="flex"
                                          gap={10}
                                          mt={0.5}
                                          mb={0.5}
                                        >
                                          <Box display="flex" gap={2}>
                                            {unit.type?.toLowerCase() ===
                                            "tower" ? (
                                              <Typography>
                                                Floor : {unit.floor}
                                              </Typography>
                                            ) : (
                                              <Typography>
                                                Block : {unit.floor}
                                              </Typography>
                                            )}
                                          </Box>
                                          <Box display="flex" gap={2}>
                                            {unit.type?.toLowerCase() ===
                                            "tower" ? (
                                              <Typography>
                                                Flat : {unit.flat_number}
                                              </Typography>
                                            ) : (
                                              <Typography>
                                                Villa : {unit.flat_number}
                                              </Typography>
                                            )}
                                          </Box>
                                        </Box>
                                        {unit.owner && (
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            className="notranslate"
                                          >
                                            <Avatar
                                              src={
                                                unit.owner.avatar_url ||
                                                undefined
                                              }
                                              sx={{
                                                width: 24,
                                                height: 24,
                                                fontSize: 13,
                                                bgcolor: unit.owner.avatar_url
                                                  ? undefined
                                                  : stringToColor(
                                                      unit.owner.email || "O"
                                                    ),
                                              }}
                                            >
                                              {unit.owner.avatar_url
                                                ? ""
                                                : unit.owner.email?.[0]?.toUpperCase() ||
                                                  "O"}
                                            </Avatar>
                                            <Typography
                                              variant="body2"
                                              fontWeight={500}
                                            >
                                              {unit.owner.email || "No Email"}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                      <Chip
                                        label={`${unit.status}`}
                                        size="small"
                                        color={
                                          unit.status.toLowerCase() === "vacant"
                                            ? "error"
                                            : "success"
                                        }
                                      />
                                    </Box>
                                  </React.Fragment>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    ));
                  })()
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          Confirm
        </Button>
        <Button onClick={onClose} variant="outlined" disabled={loading}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignLandlordPropertiesDialog;
